import instance from "@/store/actions/instance";
import { Button, message, Spin } from "antd";
import { useEffect, useRef, useState } from "react";
import { withNamespaces } from "react-i18next";
import { connect } from "react-redux";
import WhatsappChatMessageItem from "./WhatsappChatMessageItem";
import { SendOutlined } from "@ant-design/icons";
import TextArea from "antd/lib/input/TextArea";
import { SET_HAS_UNREAD_WHATSAPP_MESSAGES } from "@/store/actions/actionTypes";
import moment from "moment";

function WhatsappChatMessages({
  selectedChat,
  t,
  config,
  chats,
  setChats,
  hasUnreadWhatsappMessages,
  setHasUnreadWhatsappMessages,
}) {
  const messagesContainerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [isWithin24h, setIsWithin24h] = useState(true);
  const [sendingLoading, setSendingLoading] = useState(false);
  const [messageText, setMessageText] = useState("");

  const [messages, setMessages] = useState(null);
  // const [sentMessages, setSentMessages] = useState([]);
  const isRTL = config?.direction === "rtl";

  const markChatAsRead = () => {
    const newChats = [...chats];
    const newChat = chats.find(
      (c) =>
        c.customer_number == selectedChat.customer_number &&
        c.customer_name === selectedChat.customer_name
    );
    newChat.has_unread = false;
    setChats(newChats);
    if (hasUnreadWhatsappMessages && !newChats.some((c) => c.has_unread)) {
      setHasUnreadWhatsappMessages(false);
    }
  };

  const fetchMessages = async (afterDate = null) => {
    try {
      const res = await instance.get("/facebook/messages", {
        params: {
          user_phone_number: selectedChat.customer_number,
          after_date: afterDate,
        },
      });
      setMessages([...(messages || []), ...res.data]);
      if (selectedChat.has_unread) {
        markChatAsRead();
      }

      if (res.data.length) {
        setIsWithin24h(
          moment.utc().diff(moment.utc(res.data.at(-1).created_at), "hours") <
            24
        );
        setTimeout(() => {
          messagesContainerRef.current?.scrollIntoView();
        }, 0);
      }
    } catch (err) {
      if (err.response && err.response.status == 403)
        message.error(t("AccessDeniedMessage"));
      else message.error(t("SomethingWentWrong"));
    }
    setLoading(false);
  };

  const sendMessage = async () => {
    try {
      setSendingLoading(true);
      const res = await instance.post("/facebook/messages/send/", {
        customer_phone_number: selectedChat.customer_number,
        customer_name: selectedChat.customer_name,
        message_text: messageText,
      });
      setMessageText("");
      await fetchMessages(messages.at(-1).created_at);
      // sentMessages.push({
      //   meta_id: res.data,
      //   customer_name: null,
      //   text: messageText,
      //   created_at: "2025-12-14 21:58:19",
      //   is_from_merchant: 1,
      //   status: "read",
      // });
    } catch (err) {
      if (err.response && err.response.status == 403)
        message.error(t("AccessDeniedMessage"));
      else message.error(t("Failed to send message."));
    }
    setSendingLoading(false);
  };

  useEffect(() => {
    if (!selectedChat) return;

    if (!messages) {
      fetchMessages();
    }
    const interval = setInterval(async () => {
      fetchMessages(messages.at(-1).created_at);
    }, 1000 * 60 * 1);

    return () => clearInterval(interval);
  }, [selectedChat, messages]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%", // or 100vh if this is a full page
      }}
    >
      <div
        style={{
          flex: 1,
          overflowY: "auto",
          padding: 10,
          border: "1px solid #f0f0f0",
          borderRadius: 10,
          background: "#fff",
          overscrollBehavior: "contain",
          WebkitOverflowScrolling: "touch",
          direction: isRTL ? "rtl" : "ltr",
          textAlign: isRTL ? "right" : "left",
        }}
      >
        {loading ? (
          <div style={{ padding: 24, textAlign: "center" }}>
            <Spin />
          </div>
        ) : messages?.length ? (
          messages.map((message) => (
            <WhatsappChatMessageItem key={message.id} message={message} />
          ))
        ) : (
          <div style={{ padding: 24, color: "#999" }}>
            {t("No messages yet")}
          </div>
        )}
        <div ref={messagesContainerRef} />
      </div>
      <div
        style={{
          padding: 10,
          borderTop: "1px solid #eee",
          border: "none",
          direction: isRTL ? "rtl" : "ltr",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            maxWidth: "100%",
            flexDirection: isRTL ? "row-reverse" : "row",
          }}
        >
          {/* rounded container */}
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: 8,
              borderRadius: 999,
              background: "#fff",
              boxShadow: "0 4px 12px rgba(0,0,0,.06)",
              border: "1px solid #eaeaea",
            }}
          >
            <div style={{ flex: 1, position: "relative" }}>
              <TextArea
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                onPressEnter={(e) => {
                  if (!e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                autoSize={{ minRows: 1, maxRows: 6 }}
                placeholder={t("Write a message…")}
                style={{
                  flex: 1,
                  border: "none",
                  boxShadow: "none",
                  direction: isRTL ? "rtl" : "ltr",
                  textAlign: isRTL ? "right" : "left",
                  background: "transparent",
                }}
                disabled={!isWithin24h} // Disable typing if outside 24h
              />
            </div>

            <Button
              type="primary"
              shape="round"
              icon={<SendOutlined />}
              onClick={sendMessage}
              loading={sendingLoading}
              disabled={!messageText?.trim?.() || !isWithin24h} // Disable if no text or outside 24h
            >
              {t("Send")}
            </Button>
          </div>
        </div>

        {!isWithin24h && (
          <div
            style={{
              marginTop: 8,
              color: "#d32f2f",
              fontSize: 14,
            }}
          >
            {t(
              "You can't send a message right now because it's been over 24 hours since the customer last contacted you."
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  config: state.config,
  is_staff: state.auth.is_staff,
  username: state.auth.username,
  hasUnreadWhatsappMessages: state.integration.hasUnreadWhatsappMessages,
});

const mapDispatchToProps = (dispatch) => {
  return {
    setHasUnreadWhatsappMessages: (payload) =>
      dispatch({ type: SET_HAS_UNREAD_WHATSAPP_MESSAGES, payload }),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withNamespaces()(WhatsappChatMessages));
